'use client'

import { AnimatePresence, LazyMotion, domAnimation } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { MenuVariant } from '@/types/menu.types'
import { menuConfig } from '@/ui/components/navbar/config'
import { DesktopCta } from './desktop/cta'
import { DesktopMenu } from './desktop/menu'
import HamburgerButton from './mobile/hamburgerButton'
import { MobileMenu } from './mobile/menu'

type NavbarProps = {
  logo?: string
  variant: MenuVariant
}

export default function Navbar({
  variant = 'main' as MenuVariant,
  logo = '/images/logo/logo-white.svg',
}: NavbarProps) {
  const [isMobileMenu, setMobileMenu] = useState(false)
  const [mounted, setMounted] = useState(false)

  const tone = menuConfig[variant]?.tone ?? 'dark'

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', isMobileMenu)
    return () => document.body.classList.remove('overflow-hidden')
  }, [isMobileMenu])

  const handleClose = () => setMobileMenu(false)

  return (
    <>
      <nav
        aria-label="Navigation principale"
        className={`relative z-50 h-20 desktop:h-24 ${
          tone === 'light' ? 'border-b border-trace' : ''
        }`}
      >
        <div className="shell flex h-full items-center justify-between gap-8">
          <Link href="/" aria-label="Page d'accueil" onClick={handleClose}>
            <p className="sr-only">
              Nico Bat - Des solutions sur mesure pour un impact durable
            </p>
            <Image
              src={logo}
              alt="Logo Nico Bat"
              width={150}
              height={64}
              draggable={false}
              priority
              unoptimized
              className="h-11 w-auto desktop:h-12"
            />
          </Link>

          <DesktopMenu variant={variant} />

          <div className="flex items-center gap-2">
            <DesktopCta tone={tone} />
            <HamburgerButton
              isOpen={isMobileMenu}
              onToggle={() => setMobileMenu((prev) => !prev)}
              variant={variant}
            />
          </div>
        </div>
      </nav>

      {mounted &&
        createPortal(
          <LazyMotion features={domAnimation}>
            <AnimatePresence>
              {isMobileMenu && (
                <MobileMenu
                  key="mobile-menu"
                  onToggle={handleClose}
                  variant={variant}
                />
              )}
            </AnimatePresence>
          </LazyMotion>,
          document.body
        )}
    </>
  )
}
